import CatalogProductCard from "./CatalogProductCard.tsx";
import { t, useStoreLanguage, getLocale } from "../../../features/store-language/i18n.ts";
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import {
  useLocation,
  useNavigationType,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Empty, Select, Button, Drawer } from "antd";
import {
  CaretUp,
  FunnelSimple,
  X,
  CaretDown,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import ContentLoader from "react-content-loader";
import SchemaFilter, { SelectedFilter } from "./SchemaFilter.tsx";
import AppFeedback from "../../shared/AppFeedback.tsx";
import "./schemaList.css";
import "./schemaProductCard.css";
import "./schemaFilterGallery.css";
import { getStoreProducts } from "../storefrontService.ts";
import { createFilterValueSearchParams, type StoreFilterOptions as FilterOptions } from "../storefrontFilters.ts";
import CategoryNavigation from "../Category/CategoryNavigation.tsx";
import { buildProductPath, buildVariantPath } from "../productUrl.ts";

const SCHEMA_PAGE_SIZE = 20;

interface CardPreviewOption {
  variantId: number;
  addressTitle: string;
  colorId: number | null;
  dimensionId: number | null;
  dimensionTitle: string | null;
  fileId: string | null;
}

interface SchemaListSnapshot {
  currentPage: number;
  data: any[];
  filters: FilterOptions;
  hasMore: boolean;
  isFiltered: boolean;
  scrollTop: number;
  selectedCardId?: string;
  selectedCardViewportTop?: number;
  selectedVariantIds: { [schemaId: number]: number | null };
  selectedFilters: SelectedFilter[];
  sortBy: "default" | "titleAsc" | "titleDesc";
}

const EMPTY_HOME_FILTERS: FilterOptions = {};
const schemaListSnapshots = new Map<string, SchemaListSnapshot>();
const MAX_SCHEMA_LIST_SNAPSHOTS = 8;

function setSchemaListSnapshot(key: string, snapshot: SchemaListSnapshot) {
  schemaListSnapshots.delete(key);
  schemaListSnapshots.set(key, snapshot);

  while (schemaListSnapshots.size > MAX_SCHEMA_LIST_SNAPSHOTS) {
    const oldestKey = schemaListSnapshots.keys().next().value;
    if (oldestKey === undefined) {
      break;
    }
    schemaListSnapshots.delete(oldestKey);
  }
}

function getPageScrollTop() {
  const scrollContainer = document.getElementById("scrollableContainer");
  return Math.max(scrollContainer?.scrollTop || 0, window.scrollY);
}

function normalizeFilters(filters: FilterOptions) {
  return Object.keys(filters)
    .sort()
    .reduce<Record<string, unknown>>(function (result, key) {
      const value = filters[key as keyof FilterOptions];
      if (value !== undefined) {
        result[key] = value;
      }
      return result;
    }, {});
}

function areFiltersEqual(first: FilterOptions, second: FilterOptions) {
  return (
    JSON.stringify(normalizeFilters(first)) ===
    JSON.stringify(normalizeFilters(second))
  );
}

function createPreviewFilterSearchParams(filters: FilterOptions) {
  return createFilterValueSearchParams(filters);
}

function hasActiveFilters(filters: FilterOptions) {
  return Object.values(filters).some(function (value) {
    return Array.isArray(value) ? value.length > 0 : typeof value === "string" && value.trim().length > 0;
  });
}

function getCategoryTitleFromSlug(slug?: string) {
  if (!slug) {
    return t("محصولات دسته‌بندی");
  }

  let decodedSlug = slug;
  try {
    decodedSlug = decodeURIComponent(slug);
  } catch {
    // React Router normally decodes params; retain malformed input safely.
  }

  return decodedSlug.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

// Skeleton loader component matching the card structure
function SchemaCardSkeleton() {
  useStoreLanguage();
  return (
    <ContentLoader
      speed={2}
      width={280}
      height={420}
      viewBox="0 0 280 420"
      backgroundColor="#edf1f6"
      foregroundColor="#f8fafc"
      className="schema-card-skeleton"
    >
      <rect x="0" y="0" rx="18" ry="18" width="280" height="315" />
      <rect x="16" y="336" rx="4" ry="4" width="92" height="12" />
      <rect x="16" y="360" rx="5" ry="5" width="220" height="18" />
      <rect x="16" y="392" rx="5" ry="5" width="130" height="12" />
    </ContentLoader>
  );
}

interface InfiniteSchemaScrollOptions {
  enabled: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

function useInfiniteSchemaScroll({
  enabled,
  isLoading,
  onLoadMore,
}: InfiniteSchemaScrollOptions) {
  const storeLanguage = useStoreLanguage();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(
    function () {
      const sentinel = sentinelRef.current;
      if (!sentinel || !enabled || isLoading) {
        return undefined;
      }

      const observer = new IntersectionObserver(
        function (entries) {
          if (entries[0]?.isIntersecting) {
            onLoadMoreRef.current();
          }
        },
        {
          root: null,
          rootMargin: "0px 0px 120px 0px",
          threshold: 0,
        },
      );
      observer.observe(sentinel);

      return function () {
        observer.disconnect();
      };
    },
    [storeLanguage, enabled, isLoading],
  );

  return sentinelRef;
}

interface PagedResponse {
  total?: number;
  data?: any[];
  Data?: any[];
  page?: number;
  Page?: number;
  pageSize?: number;
  PageSize?: number;
}

const SchemaList = () => {
  const storeLanguage = useStoreLanguage();
  const requestSequence = useRef(0);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const navigationType = useNavigationType();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  useLayoutEffect(function () {
    const sidebar = sidebarRef.current;
    if (!sidebar) {
      return;
    }

    let frame = 0;
    function updateSidebarHeight() {
      frame = 0;
      if (!sidebar.getClientRects().length) {
        return;
      }
      const viewport = window.visualViewport;
      const viewportBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      const height = `${Math.max(0, Math.floor(viewportBottom - sidebar.getBoundingClientRect().top - 12))}px`;
      if (sidebar.style.getPropertyValue("--schema-filter-viewport-height") !== height) {
        sidebar.style.setProperty("--schema-filter-viewport-height", height);
      }
    }
    function scheduleSidebarHeight() {
      if (!frame) {
        frame = window.requestAnimationFrame(updateSidebarHeight);
      }
    }

    updateSidebarHeight();
    const observer = new ResizeObserver(scheduleSidebarHeight);
    const container = sidebar.closest(".schema-preview-container");
    const layout = sidebar.closest(".shop-layout");
    if (container) {
      observer.observe(container);
    }
    layout?.querySelectorAll(".shop-header, .shop-breadcrumb").forEach(function (element) {
      observer.observe(element);
    });
    window.addEventListener("scroll", scheduleSidebarHeight, { passive: true, capture: true });
    window.addEventListener("resize", scheduleSidebarHeight);
    window.visualViewport?.addEventListener("resize", scheduleSidebarHeight);
    window.visualViewport?.addEventListener("scroll", scheduleSidebarHeight);
    return function () {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleSidebarHeight, true);
      window.removeEventListener("resize", scheduleSidebarHeight);
      window.visualViewport?.removeEventListener("resize", scheduleSidebarHeight);
      window.visualViewport?.removeEventListener("scroll", scheduleSidebarHeight);
      sidebar.style.removeProperty("--schema-filter-viewport-height");
    };
  }, []);
  useEffect(function () {
    if (searchParams.get("legacyLink") === "1") {
      void AppFeedback.info({ title: t("پیوند قدیمی کالا"), description: t("این پیوند از شناسهٔ قدیمی استفاده می‌کند؛ کالای مورد نظر را از فهرست انتخاب کنید.") });
    }
  }, [storeLanguage, searchParams]);
  const searchQuery = searchParams.get("q") || "";
  const breadcrumbTitle = searchQuery
    ? t("نتایج جست‌وجو")
    : slug
      ? getCategoryTitleFromSlug(slug)
      : t("همهٔ محصولات");
  const restoredSnapshot = useMemo(
    function () {
      return navigationType === "POP"
        ? schemaListSnapshots.get(`${storeLanguage}:${location.key}`)
        : undefined;
    },
    [storeLanguage, location.key, navigationType],
  );
  const [filteredData, setFilteredData] = useState<any[]>(function () {
    return restoredSnapshot?.data || [];
  });
  const [loading, setLoading] = useState(!restoredSnapshot);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>(function () {
    return restoredSnapshot?.filters || {};
  });
  const [isFiltered, setIsFiltered] = useState(
    restoredSnapshot?.isFiltered || false,
  );
  const [currentPage, setCurrentPage] = useState(
    restoredSnapshot?.currentPage || 1,
  );
  const [hasMore, setHasMore] = useState(restoredSnapshot?.hasMore ?? true);
  const isLoadingRef = useRef(false);
  const restoredFiltersRef = useRef<FilterOptions | null>(
    restoredSnapshot?.filters || null,
  );
  const hasRestoredScrollRef = useRef(false);
  // Use refs to track current values and avoid stale closures
  const filtersRef = useRef<FilterOptions>(restoredSnapshot?.filters || {});
  const currentPageRef = useRef(restoredSnapshot?.currentPage || 1);
  const hasMoreRef = useRef(restoredSnapshot?.hasMore ?? true);
  const previousSlugRef = useRef(slug);

  useEffect(function () {
    document.body.classList.add("schema-list-page-active");

    return function () {
      document.body.classList.remove("schema-list-page-active");
    };
  }, [storeLanguage]);

  // Selected filters for badge display
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(
    function () {
      return restoredSnapshot?.selectedFilters || [];
    },
  );
  const [clearFilterKey, setClearFilterKey] = useState<
    keyof typeof filters | null
  >(null);
  const [clearFilterId, setClearFilterId] = useState<number | null>(null);
  const [clearFilterVersion, setClearFilterVersion] = useState(0);

  // Preserve the exact displayed variant when returning to the catalog.
  const [selectedVariantIds, setSelectedVariantIds] = useState<{
    [schemaId: number]: number | null;
  }>(function () {
    return restoredSnapshot?.selectedVariantIds || {};
  });

  // Initial search term from URL query parameter
  const [initialSearchTerm, setInitialSearchTerm] = useState<
    string | undefined
  >(searchQuery || undefined);

  // Filter drawer state for mobile/tablet
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState<"default" | "titleAsc" | "titleDesc">(
    restoredSnapshot?.sortBy || "default",
  );
  const sortRef = useRef(sortBy);
  useEffect(function () {
    return function () { requestSequence.current++; };
  }, [storeLanguage]);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const visibleData = filteredData;

  // Handle selected filters change from SchemaFilter
  const handleSelectedFiltersChange = useCallback(
    (filters: SelectedFilter[]) => {
      setSelectedFilters(filters);
    },
    [],
  );

  // Handle clearing a specific filter badge
  const handleClearFilterBadge = useCallback(
    (key: keyof typeof filters, id: number) => {
      setSelectedFilters(function (previousFilters) {
        return previousFilters.filter(function (filter) {
          return !(filter.key === key && filter.id === id);
        });
      });
      setClearFilterKey(key);
      setClearFilterId(id);
      setClearFilterVersion(function (version) {
        return version + 1;
      });
    },
    [],
  );

  const activeFilterCount = selectedFilters.length;

  const snapshotStateRef = useRef<SchemaListSnapshot>({
    currentPage,
    data: filteredData,
    filters,
    hasMore,
    isFiltered,
    scrollTop: restoredSnapshot?.scrollTop || 0,
    selectedCardId: restoredSnapshot?.selectedCardId,
    selectedCardViewportTop: restoredSnapshot?.selectedCardViewportTop,
    selectedVariantIds,
    selectedFilters,
    sortBy,
  });
  snapshotStateRef.current = {
    ...snapshotStateRef.current,
    currentPage,
    data: filteredData,
    filters,
    hasMore,
    isFiltered,
    selectedVariantIds,
    selectedFilters,
    sortBy,
  };

  const saveListSnapshot = useCallback(
    function () {
      setSchemaListSnapshot(`${storeLanguage}:${location.key}`, {
        ...snapshotStateRef.current,
        scrollTop: getPageScrollTop(),
      });
    },
    [storeLanguage, location.key],
  );

  function openSchema(item: any, cardElement: HTMLElement, selectedOption?: CardPreviewOption) {
    snapshotStateRef.current = {
      ...snapshotStateRef.current,
      selectedCardId: String(item.id),
      selectedCardViewportTop: cardElement.getBoundingClientRect().top,
    };
    saveListSnapshot();
    const previewSearchParams = createPreviewFilterSearchParams(
      filtersRef.current,
    );
    if (hasActiveFilters(filtersRef.current) && selectedOption) {
      navigate(selectedOption.addressTitle
        ? buildVariantPath(item.slug, selectedOption.addressTitle)
        : `/products/${encodeURIComponent(item.slug)}/variants/${selectedOption.variantId}`);
      return;
    }
    if (slug) previewSearchParams.set("categorySlug", slug);
    navigate(buildProductPath(item.slug, previewSearchParams));
  }

  useEffect(
    function () {
      return function () {
        saveListSnapshot();
      };
    },
    [storeLanguage, saveListSnapshot],
  );

  useLayoutEffect(
    function () {
      if (
        !restoredSnapshot ||
        filteredData.length === 0 ||
        hasRestoredScrollRef.current
      ) {
        return undefined;
      }

      hasRestoredScrollRef.current = true;
      let secondFrame = 0;
      const firstFrame = window.requestAnimationFrame(function () {
        secondFrame = window.requestAnimationFrame(function () {
          const scrollContainer = document.getElementById(
            "scrollableContainer",
          );
          const selectedCard = restoredSnapshot.selectedCardId
            ? document.getElementById(
                `schema-card-${restoredSnapshot.selectedCardId}`,
              )
            : null;

          if (
            selectedCard &&
            restoredSnapshot.selectedCardViewportTop !== undefined
          ) {
            const offset =
              selectedCard.getBoundingClientRect().top -
              restoredSnapshot.selectedCardViewportTop;
            if (
              scrollContainer &&
              scrollContainer.scrollHeight > scrollContainer.clientHeight
            ) {
              scrollContainer.scrollTop += offset;
            } else {
              window.scrollBy({ top: offset, behavior: "auto" });
            }
            return;
          }

          if (
            scrollContainer &&
            scrollContainer.scrollHeight > scrollContainer.clientHeight
          ) {
            scrollContainer.scrollTo({
              top: restoredSnapshot.scrollTop,
              behavior: "auto",
            });
          } else {
            window.scrollTo({
              top: restoredSnapshot.scrollTop,
              behavior: "auto",
            });
          }
        });
      });

      return function () {
        window.cancelAnimationFrame(firstFrame);
        window.cancelAnimationFrame(secondFrame);
      };
    },
    [filteredData.length, restoredSnapshot],
  );

  function handleScrollToTop() {
    const scrollContainer = document.getElementById("scrollableContainer");
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? "auto"
      : "smooth";

    scrollContainer?.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, behavior });
  }

  useEffect(function () {
    const scrollContainer = document.getElementById("scrollableContainer");

    function updateScrollButtonVisibility() {
      const scrollTop = Math.max(
        scrollContainer?.scrollTop || 0,
        window.scrollY,
      );
      setShowScrollToTop(scrollTop > 420);
    }

    scrollContainer?.addEventListener("scroll", updateScrollButtonVisibility, {
      passive: true,
    });
    window.addEventListener("scroll", updateScrollButtonVisibility, {
      passive: true,
    });
    updateScrollButtonVisibility();

    return function () {
      scrollContainer?.removeEventListener(
        "scroll",
        updateScrollButtonVisibility,
      );
      window.removeEventListener("scroll", updateScrollButtonVisibility);
    };
  }, [storeLanguage]);

  // Keep refs in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [storeLanguage, filters]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [storeLanguage, currentPage]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [storeLanguage, hasMore]);

  // Reset pagination and data when slug changes
  useEffect(() => {
    if (previousSlugRef.current !== slug) {
      previousSlugRef.current = slug;
      // Reset pagination and data for new category
      setCurrentPage(1);
      currentPageRef.current = 1;
      setHasMore(true);
      hasMoreRef.current = true;
      setFilteredData([]);
      setInitialSearchTerm(undefined);
    }
  }, [storeLanguage, slug]);

  // Handle search query parameter (with or without slug)
  useEffect(() => {
    if (searchQuery) {
      // Set initial search term for SchemaFilter - it will call onFilterChange which triggers API
      setInitialSearchTerm(searchQuery);
    } else {
      // Clear search term if no search query
      setInitialSearchTerm(undefined);
    }
  }, [storeLanguage, searchQuery]);

  const getSchema = useCallback(
    async (
      filtersToApply?: FilterOptions,
      page: number = 1,
      append: boolean = false,
      slugValue?: string,
    ) => {
      void storeLanguage; // Locale is part of this read/translation cache key.
      const sequence = ++requestSequence.current;
      try {
        if (append) {
          setLoadingMore(true);
          setLoadMoreError(false);
          isLoadingRef.current = true;
        } else {
          setLoading(true);
          isLoadingRef.current = true;
        }
        // Always use the provided filters, or current filters from ref (most up-to-date)
        const appliedFilters = filtersToApply || filtersRef.current;

        // Check if any filter is applied
        const filterKeys = Object.keys(appliedFilters);
        const hasFilters = filterKeys.some(
          (key) =>
            appliedFilters[key as keyof FilterOptions] !== undefined &&
            appliedFilters[key as keyof FilterOptions] !== null,
        );
        setIsFiltered(hasFilters);

        // Build query parameters - always include pagination
        const queryParams = createFilterValueSearchParams(appliedFilters);

        // Add slug if available
        if (slugValue) {
          queryParams.append("slug", slugValue);
        }

        if (appliedFilters.searchTerm) {
          queryParams.set("SearchTerm", appliedFilters.searchTerm);
        }

        // Always add pagination parameters
        queryParams.append("page", page.toString());
        queryParams.append("pageSize", SCHEMA_PAGE_SIZE.toString());
        queryParams.append("sortBy", sortRef.current);

        const response: PagedResponse = await getStoreProducts(queryParams);
        if (sequence !== requestSequence.current) { return; }

        // Handle both camelCase and PascalCase response formats
        const data = response.Data || response.data || [];
        const responsePage = response.Page ?? response.page ?? page;

        // If we have data, show it. If no data, we've reached the end.
        const hasData = data.length > 0;

        if (append) {
          if (hasData) {
            setFilteredData(function (previousItems) {
              const existingIds = new Set(
                previousItems.map(function (item) {
                  return item.id;
                }),
              );
              const newItems = data.filter(function (item) {
                return !existingIds.has(item.id);
              });
              return [...previousItems, ...newItems];
            });
          }
        } else {
          setFilteredData(data);
          setSelectedVariantIds({});
        }

        const resolvedPage = Math.max(page, responsePage);
        currentPageRef.current = resolvedPage;
        const more = response.total === undefined ? hasData : resolvedPage * SCHEMA_PAGE_SIZE < response.total;
        hasMoreRef.current = more;
        filtersRef.current = appliedFilters;
        setCurrentPage(resolvedPage);
        setHasMore(more);
      } catch (error) {
        if (sequence !== requestSequence.current) { return; }
        if (append) {
          setLoadMoreError(true);
        } else {
          setFilteredData([]);
        }
      } finally {
        if (sequence === requestSequence.current) {
          setLoading(false);
          setLoadingMore(false);
          isLoadingRef.current = false;
        }
      }
    },
    [storeLanguage],
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterOptions) => {
      void storeLanguage; // Locale is part of this read/translation cache key.
      if (
        restoredFiltersRef.current &&
        areFiltersEqual(restoredFiltersRef.current, newFilters)
      ) {
        return;
      }

      restoredFiltersRef.current = null;
      filtersRef.current = newFilters;
      currentPageRef.current = 1;
      hasMoreRef.current = true;
      setFilters(newFilters);
      setCurrentPage(1);
      setHasMore(true);
      setLoadMoreError(false);
      // Call getSchema directly with new filters to avoid duplicate calls
      getSchema(newFilters, 1, false, slug);
    },
    [storeLanguage, getSchema, slug],
  );

  const fetchMoreData = useCallback(() => {
      void storeLanguage; // Locale is part of this read/translation cache key.
    // Use refs to get current values (avoid stale closures)
    const currentFilters = filtersRef.current;
    const currentPageValue = currentPageRef.current;
    const hasMoreValue = hasMoreRef.current;

    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    if (!hasMoreValue) {
      return;
    }

    // Set loading flag immediately to prevent duplicate calls
    isLoadingRef.current = true;
    setLoadingMore(true);

    const nextPage = currentPageValue + 1;
    void getSchema(currentFilters, nextPage, true, slug);
  }, [storeLanguage, getSchema, slug]);

  const infiniteScrollSentinelRef = useInfiniteSchemaScroll({
    enabled: hasMore && !loadMoreError && filteredData.length > 0,
    isLoading: loading || loadingMore,
    onLoadMore: fetchMoreData,
  });

  function retryLoadingMore() {
    setLoadMoreError(false);
    fetchMoreData();
  }

  return (
    <>
      <CategoryNavigation
        slug={slug}
        title={breadcrumbTitle}
        actions={(
          <>
            <Button
              icon={<FunnelSimple weight="bold" />}
              onClick={function () {
                setFilterDrawerOpen(true);
              }}
              className="schema-filter-toggle-btn"
            >
              {t("فیلتر")}
              {activeFilterCount > 0 && (
                <span className="schema-filter-count">
                  {activeFilterCount.toLocaleString(getLocale())}
                </span>
              )}
            </Button>
            <div className="schema-sort-control">
              <span className="schema-sort-control__label">{t("مرتب‌سازی")}</span>
              <Select
                value={sortBy}
                onChange={function (value) {
                  sortRef.current = value;
                  setSortBy(value);
                  restoredFiltersRef.current = null;
                  handleFilterChange(filtersRef.current);
                }}
                loading={loading}
                disabled={loading}
                suffixIcon={<CaretDown weight="bold" />}
                aria-label={t("مرتب‌سازی نتایج")}
                options={[
                  { value: "default", label: t("ترتیب پیش‌فرض") },
                  { value: "titleAsc", label: t("نام: الف تا ی") },
                  { value: "titleDesc", label: t("نام: ی تا الف") },
                ]}
              />
            </div>
          </>
        )}
      />

      <div className="schema-preview-container">
        <div
          className={`schema-grid-controls${selectedFilters.length === 0 ? " schema-grid-controls--empty" : ""}`}
          role="region"
          aria-label={t("ابزارهای مرتب‌سازی و فیلتر محصولات")}
        >
          <div className="schema-active-filters">
            {selectedFilters.map((filter) => (
              <span key={`${filter.key}-${filter.id}`} className="filter-badge">
                <span className="filter-badge-category">{filter.category}:</span>
                <span className="filter-badge-label">{filter.label}</span>
                <button
                  type="button"
                  className="filter-badge-close"
                  onClick={function () {
                    handleClearFilterBadge(filter.key, filter.id);
                  }}
                  aria-label={t("حذف فیلتر {{value0}}", { value0: filter.label })}
                >
                  <X weight="bold" />
                </button>
              </span>
            ))}
          </div>
        </div>
        <div className="schema-layout">
          <div
            className="schema-sidebar"
            ref={sidebarRef}
          >
            <SchemaFilter
              onFilterChange={handleFilterChange}
              onSelectedFiltersChange={handleSelectedFiltersChange}
              clearFilterKey={clearFilterKey}
              clearFilterId={clearFilterId}
              clearFilterVersion={clearFilterVersion}
              slug={slug}
              initialSearchTerm={initialSearchTerm}
              initialFilters={isHome ? restoredSnapshot?.filters ?? EMPTY_HOME_FILTERS : undefined}
              persistFilters={!isHome}
              resultsLoading={loading}
            />
          </div>
          <div className="schema-main-content">
            {loading ? (
              <div
                className="schema-grid"
              >
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="schema-card-wrapper">
                    <SchemaCardSkeleton />
                  </div>
                ))}
              </div>
            ) : filteredData.length > 0 ? (
              <div className="schema-results-feed" aria-busy={loadingMore}>
                <div
                  className="schema-grid"
                >
                  {visibleData.map((item, index) => {
                    const options: CardPreviewOption[] = item.previewOptions || [];
                    const selectedOption = options.find(function (option) {
                      return option.variantId === selectedVariantIds[item.id];
                    }) || options.find(function (option) {
                      return option.variantId === item.matchedVariantId;
                    }) || options.find(function (option) {
                      return option.fileId && option.fileId === item.fileId;
                    }) || options.find(function (option) {
                      return Boolean(option.fileId);
                    }) || options[0];
                    function selectOption(option: CardPreviewOption | undefined) {
                      if (option?.fileId) {
                        setSelectedVariantIds(function (previous) {
                          return { ...previous, [item.id]: option.variantId };
                        });
                      }
                    }
                    return (
                      <motion.div
                        key={String(item.id)}
                        initial={{ opacity: 0, y: 12, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{
                          duration: 0.28,
                          delay: Math.min(index % SCHEMA_PAGE_SIZE, 5) * 0.035,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="schema-card-wrapper"
                      >
                        <CatalogProductCard
                          id={`schema-card-${item.id}`}
                          title={item.title}
                          collection={item.paletteTitle || t("پالت منتخب")}
                          colors={item.colorFiles || []}
                          options={options}
                          selectedOption={selectedOption}
                          onSelect={selectOption}
                          onOpen={function (element) {
                            openSchema(item, element, selectedOption);
                          }}
                        />
                      </motion.div>
                    );
                  })}
                </div>

                <AnimatePresence initial={false}>
                  {loadingMore ? (
                    <motion.section
                      key="loading-more"
                      className="schema-load-more"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      role="status"
                      aria-live="polite"
                    >
                      <div className="schema-load-more__message">
                        <span
                          className="schema-load-more__spinner"
                          aria-hidden="true"
                        />
                        <span>
                          <strong>{t("در حال آماده‌سازی طرح‌های بیشتر")}</strong>
                          <small>{t("لطفاً چند لحظه منتظر بمانید…")}</small>
                        </span>
                      </div>
                    </motion.section>
                  ) : null}
                </AnimatePresence>

                <div
                  ref={infiniteScrollSentinelRef}
                  className="schema-infinite-scroll-sentinel"
                  aria-hidden="true"
                />

                {loadMoreError ? (
                  <div
                    className="schema-load-more-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    <span>{t("بارگذاری طرح‌های بیشتر انجام نشد.")}</span>
                    <Button type="default" onClick={retryLoadingMore}>{t("تلاش دوباره")} </Button>
                  </div>
                ) : null}

                {!hasMore && !loadMoreError ? (
                  <div className="schema-results-end" role="status">
                    <span aria-hidden="true">✓</span>{t("همهٔ طرح‌ها را مشاهده کردید")} </div>
                ) : null}
              </div>
            ) : (
              <Empty description={t("نقشه‌ای یافت نشد")} className="schema-empty" />
            )}
          </div>
        </div>
      </div>

      {/* Filter Drawer for Mobile/Tablet */}
      <Drawer
        title={t("فیلترها")}
        placement="bottom"
        onClose={function () {
          setFilterDrawerOpen(false);
        }}
        open={filterDrawerOpen}
        height="100dvh"
        className="schema-filter-drawer"
        zIndex={1000}
        mask={true}
        maskClosable={true}
        destroyOnClose={false}
      >
        <SchemaFilter
          onFilterChange={handleFilterChange}
          onSelectedFiltersChange={handleSelectedFiltersChange}
          clearFilterKey={clearFilterKey}
          clearFilterId={clearFilterId}
          clearFilterVersion={clearFilterVersion}
          slug={slug}
          initialSearchTerm={initialSearchTerm}
          initialFilters={isHome ? filters : undefined}
          persistFilters={!isHome}
          resultsLoading={loading}
          mobileDrawer
          onMobileClose={function () {
            setFilterDrawerOpen(false);
          }}
        />
      </Drawer>

      <AnimatePresence>
        {showScrollToTop && (
          <motion.button
            type="button"
            className="schema-scroll-top-button"
            onClick={handleScrollToTop}
            initial={{ opacity: 0, scale: 0.82, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.82, y: 12 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.94 }}
          >
            <CaretUp weight="bold" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default SchemaList;
